import * as XLSX from 'xlsx-js-style'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { Lesson, LessonActivity, Player, DetailedPlayerAnalytics, Course } from '@/types'

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface RawResponse {
  id: string
  player_id: string
  activity_id: string
  response_data?: any
  content?: any
  score?: number
  feedback?: string
  submitted_at: string
}

export interface ReportData {
  lesson: Lesson
  activities: LessonActivity[]
  activityResponses: Record<string, RawResponse[]>
  players: Player[]
  playerAnalytics: Record<string, DetailedPlayerAnalytics>
}

export type LessonReportData = ReportData

export interface CourseReportData {
  course: Course
  lessons: LessonReportData[]
}

// ─── Theme-Aware PDF Colors ─────────────────────────────────────────────────

type PdfTheme = 'dark' | 'light'

export function getCurrentPdfTheme(): PdfTheme {
  if (typeof document === 'undefined') return 'light'
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark'
}

interface PdfPalette {
  pageBg: [number, number, number]
  titleColor: [number, number, number]
  subtitleColor: [number, number, number]
  bodyColor: [number, number, number]
  headerFill: [number, number, number]
  headerText: [number, number, number]
  rowAlt: [number, number, number]
  rowDefault: [number, number, number]
  borderColor: [number, number, number]
  tableFontColor: [number, number, number]
}

const PDF_PALETTES: Record<PdfTheme, PdfPalette> = {
  dark: {
    pageBg: [0, 8, 17],           // --color-bg #000811
    titleColor: [0, 255, 136],    // --color-primary #00ff88
    subtitleColor: [136, 221, 255], // --color-text #88ddff
    bodyColor: [136, 221, 255],
    headerFill: [0, 30, 60],      // dark surface
    headerText: [0, 255, 136],    // primary green
    rowAlt: [0, 15, 30],          // slightly lighter than bg
    rowDefault: [0, 8, 17],
    borderColor: [0, 60, 40],     // subtle green border
    tableFontColor: [136, 221, 255],
  },
  light: {
    pageBg: [245, 247, 250],      // --color-bg #f5f7fa
    titleColor: [5, 150, 105],    // --color-primary #059669
    subtitleColor: [51, 65, 85],  // --color-text #334155
    bodyColor: [51, 65, 85],
    headerFill: [5, 150, 105],    // primary emerald
    headerText: [255, 255, 255],
    rowAlt: [240, 242, 245],      // --color-surface-deep #f0f2f5
    rowDefault: [255, 255, 255],
    borderColor: [226, 232, 240], // --color-border #e2e8f0
    tableFontColor: [51, 65, 85],
  },
}

function getPdfPalette(theme?: PdfTheme): PdfPalette {
  return PDF_PALETTES[theme ?? getCurrentPdfTheme()]
}

function applyPdfPageStyle(doc: jsPDF, palette: PdfPalette) {
  const w = doc.internal.pageSize.getWidth()
  const h = doc.internal.pageSize.getHeight()
  doc.setFillColor(...palette.pageBg)
  doc.rect(0, 0, w, h, 'F')
}

function getAutoTableThemeStyles(palette: PdfPalette) {
  return {
    headStyles: {
      fillColor: palette.headerFill,
      textColor: palette.headerText,
      fontStyle: 'bold' as const,
    },
    bodyStyles: {
      textColor: palette.tableFontColor,
    },
    alternateRowStyles: {
      fillColor: palette.rowAlt,
    },
    styles: {
      fillColor: palette.rowDefault,
      lineColor: palette.borderColor,
      lineWidth: 0.2,
    },
    willDrawPage: (data: any) => {
      const doc = data.doc
      const w = doc.internal.pageSize.getWidth()
      const h = doc.internal.pageSize.getHeight()
      doc.setFillColor(...palette.pageBg)
      doc.rect(0, 0, w, h, 'F')
    },
  }
}

/** Add a styled section heading on the current page */
function addSectionTitle(doc: jsPDF, title: string, palette: PdfPalette, y: number = 20) {
  applyPdfPageStyle(doc, palette)
  doc.setFontSize(16)
  doc.setTextColor(...palette.titleColor)
  doc.text(title, 14, y)
  doc.setTextColor(...palette.bodyColor)
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ACTIVITY_LABELS: Record<string, string> = {
  mcq: 'MCQ',
  coding: 'Coding',
  command_shell: 'Shell',
  phishing_quiz: 'Phishing Quiz',
  packet_analysis: 'Packet Analysis',
  cyber_hygiene_quiz: 'Cyber Hygiene Quiz',
  coding_quiz: 'Coding Quiz',
  pdf_slideshow: 'Slide',
  python_repl: 'Python Coding',
  google_slides: 'Google Slides',
  youtube_video: 'YouTube Video',
}

function getContent(r: RawResponse): any {
  return r.response_data ?? r.content
}

function getPlayerName(id: string, players: Player[]): string {
  return players.find(p => p.id === id)?.display_name ?? id
}

function extractMcqResult(activity: LessonActivity, response: RawResponse) {
  const data = getContent(response)
  const options: string[] = activity.activity_data?.options ?? []
  const chosenIndex = typeof data === 'number' ? data : data?.answer
  const chosenOption =
    typeof chosenIndex === 'number' && options[chosenIndex] !== undefined
      ? options[chosenIndex]
      : String(chosenIndex ?? '')
  const correct = activity.activity_data?.correct_answer
  const isCorrect =
    correct !== undefined && chosenIndex !== undefined
      ? Number(chosenIndex) === Number(correct)
      : null
  return { chosenOption, chosenIndex, isCorrect }
}

function extractPhishingResult(response: RawResponse) {
  const data = getContent(response)
  return {
    totalCorrect: data?.total_correct ?? null,
    totalQuestions: data?.total_questions ?? null,
  }
}

function extractShellResult(activity: LessonActivity, response: RawResponse) {
  const data = getContent(response)
  const answer = typeof data === 'string' ? data : data?.answer ?? ''
  const expected = activity.activity_data?.correct_answer ?? ''
  const isCorrect = expected
    ? String(answer).trim().toLowerCase() === String(expected).trim().toLowerCase()
    : null
  return { answer: String(answer), isCorrect }
}

function extractCodingResult(response: RawResponse) {
  const data = getContent(response)
  // Handle both camelCase and snake_case shapes
  const execResult = data?.execution_result ?? data?.executionResult
  const tests = execResult?.test_results ?? execResult?.testResults ?? []
  const testsPassed = tests.filter((t: any) => t.passed).length
  const testsTotal = tests.length
  const timeTaken = data?.time_taken ?? data?.timeTaken ?? null
  const code = data?.code ?? ''
  return { testsPassed, testsTotal, timeTaken, code }
}

function formatCellValue(activity: LessonActivity, response: RawResponse): string {
  const data = getContent(response)
  if (data === null || data === undefined) return 'No response'

  switch (activity.activity_type) {
    case 'mcq': {
      const { chosenOption, chosenIndex } = extractMcqResult(activity, response)
      return typeof chosenIndex === 'number'
        ? `${String.fromCharCode(65 + chosenIndex)}. ${chosenOption}`
        : String(chosenOption)
    }
    case 'phishing_quiz': {
      const { totalCorrect } = extractPhishingResult(response)
      return totalCorrect !== null ? (totalCorrect > 0 ? 'Correct' : 'Incorrect') : 'Submitted'
    }
    case 'packet_analysis':
    case 'cyber_hygiene_quiz':
    case 'coding_quiz': {
      const answers = data?.answers
      if (Array.isArray(answers) && answers.length > 0) {
        const choice = answers[0].choice ?? answers[0].answer
        const options: string[] = activity.activity_data?.options ?? []
        const choiceIdx = Number(choice)
        if (!isNaN(choiceIdx) && options[choiceIdx] !== undefined) {
          return `${String.fromCharCode(65 + choiceIdx)}. ${options[choiceIdx]}`
        }
        return answers[0].option_text ?? String(choice ?? 'Submitted')
      }
      const { totalCorrect, totalQuestions } = extractPhishingResult(response)
      return totalCorrect !== null ? `${totalCorrect}/${totalQuestions} correct` : 'Submitted'
    }
    case 'command_shell': {
      const { answer } = extractShellResult(activity, response)
      return answer || 'Empty'
    }
    case 'coding': {
      const { testsPassed, testsTotal, code } = extractCodingResult(response)
      if (testsTotal > 0) return `${testsPassed}/${testsTotal} tests passed (${code.length} chars)`
      return code ? `Code submitted (${code.length} chars)` : 'Code submitted'
    }
    default:
      if (typeof data === 'object') {
        return String(data?.answer ?? data?.word ?? data?.value ?? JSON.stringify(data))
      }
      return String(data)
  }
}

function formatCorrectness(activity: LessonActivity, response: RawResponse): string {
  const data = getContent(response)
  if (data === null || data === undefined) return '-'

  switch (activity.activity_type) {
    case 'mcq': {
      const { isCorrect } = extractMcqResult(activity, response)
      return isCorrect === null ? 'N/A' : isCorrect ? 'Correct' : 'Incorrect'
    }
    case 'phishing_quiz': {
      const answers = data?.answers
      if (Array.isArray(answers) && answers.length > 0) {
        return answers[0].correct === true ? 'Correct' : 'Incorrect'
      }
      const { totalCorrect } = extractPhishingResult(response)
      return totalCorrect !== null ? (totalCorrect > 0 ? 'Correct' : 'Incorrect') : 'N/A'
    }
    case 'packet_analysis':
    case 'cyber_hygiene_quiz':
    case 'coding_quiz': {
      const answers = data?.answers
      if (Array.isArray(answers) && answers.length > 0) {
        return answers[0].correct === true ? 'Correct' : 'Incorrect'
      }
      return 'N/A'
    }
    case 'command_shell': {
      const { isCorrect } = extractShellResult(activity, response)
      return isCorrect === null ? 'N/A' : isCorrect ? 'Correct' : 'Incorrect'
    }
    case 'coding': {
      const { testsPassed, testsTotal } = extractCodingResult(response)
      return testsTotal > 0 ? `${testsPassed}/${testsTotal} passed` : 'N/A'
    }
    case 'python_repl': {
      const passed = data?.total_passed ?? 0
      const total = data?.total_tests ?? 0
      return total > 0 ? `${passed}/${total} passed` : 'N/A'
    }
    default:
      return 'N/A'
  }
}

function formatTimeTaken(activity: LessonActivity, response: RawResponse): string {
  const data = getContent(response)
  if (data === null || data === undefined) return '-'

  if (activity.activity_type === 'coding') {
    const { timeTaken } = extractCodingResult(response)
    if (timeTaken !== null) return `${Math.round(Number(timeTaken))}s`
  }

  // Generic: compute from activity start to submission
  if (activity.started_at && response.submitted_at) {
    const secs = Math.round(
      (new Date(response.submitted_at).getTime() - new Date(activity.started_at).getTime()) / 1000,
    )
    if (secs >= 0 && secs < 86400) return `${secs}s`
  }
  return '-'
}

/** Returns true = correct, false = incorrect, null = can't determine */
function isResponseCorrect(activity: LessonActivity, response: RawResponse): boolean | null {
  const data = getContent(response)
  if (data === null || data === undefined) return null

  switch (activity.activity_type) {
    case 'mcq': {
      const { isCorrect } = extractMcqResult(activity, response)
      return isCorrect
    }
    case 'phishing_quiz': {
      const answers = data?.answers
      if (Array.isArray(answers) && answers.length > 0) return answers.every((a: any) => a.correct)
      const tc = data?.total_correct
      return tc !== null && tc !== undefined ? tc > 0 : null
    }
    case 'packet_analysis':
    case 'cyber_hygiene_quiz':
    case 'coding_quiz': {
      const answers = data?.answers
      if (Array.isArray(answers) && answers.length > 0) return answers.every((a: any) => a.correct)
      const { totalCorrect, totalQuestions } = extractPhishingResult(response)
      if (totalCorrect !== null && totalQuestions !== null) return totalCorrect === totalQuestions
      return null
    }
    case 'command_shell': {
      const { isCorrect } = extractShellResult(activity, response)
      return isCorrect
    }
    case 'coding': {
      const { testsPassed, testsTotal } = extractCodingResult(response)
      return testsTotal > 0 ? testsPassed === testsTotal : null
    }
    case 'python_repl': {
      const passed = data?.total_passed ?? 0
      const total = data?.total_tests ?? 0
      return total > 0 ? passed === total : null
    }
    default:
      return null
  }
}

const FILL_GREEN: XLSX.CellStyle = { fill: { fgColor: { rgb: 'C6EFCE' } }, font: { color: { rgb: '006100' } } }
const FILL_RED: XLSX.CellStyle = { fill: { fgColor: { rgb: 'FFC7CE' } }, font: { color: { rgb: '9C0006' } } }
const FILL_HEADER: XLSX.CellStyle = {
  fill: { fgColor: { rgb: '3B82F6' } },
  font: { bold: true, color: { rgb: 'FFFFFF' } },
}

/** Apply header styling to first row of sheet */
function styleHeaderRow(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1')
  for (let c = range.s.c; c <= range.e.c; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c })
    if (ws[addr]) ws[addr].s = FILL_HEADER
  }
}

/** Apply green/red fill to a cell */
function styleCorrectnessCell(ws: XLSX.WorkSheet, row: number, col: number, correct: boolean | null) {
  if (correct === null) return
  const addr = XLSX.utils.encode_cell({ r: row, c: col })
  if (ws[addr]) {
    ws[addr].s = correct ? FILL_GREEN : FILL_RED
  }
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9_\- ]/g, '_').trim() || 'report'
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString()
}

function formatDurationMinutes(lesson: Lesson): string {
  if (!lesson.started_at || !lesson.ended_at) return '-'
  const secs = Math.floor(
    (new Date(lesson.ended_at).getTime() - new Date(lesson.started_at).getTime()) / 1000,
  )
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

// ─── Excel Export ────────────────────────────────────────────────────────────

export async function generateExcelReport(data: ReportData): Promise<void> {
  const { lesson, activities, activityResponses, players, playerAnalytics } = data
  const wb = XLSX.utils.book_new()

  // ── Sheet 1: Summary ──
  const totalResponses = Object.values(activityResponses).flat().length
  const possibleResponses = activities.length * players.length
  const responseRate = possibleResponses > 0 ? Math.round((totalResponses / possibleResponses) * 100) : 0

  const summaryRows = [
    ['Lesson Report'],
    [],
    ['Lesson Name', lesson.name],
    ['Join Code', lesson.run_code],
    ['Status', lesson.status],
    ['Created', formatDate(lesson.created_at)],
    ['Started', formatDate(lesson.started_at)],
    ['Ended', formatDate(lesson.ended_at)],
    ['Duration', formatDurationMinutes(lesson)],
    [],
    ['Total Students', players.length],
    ['Total Activities', activities.length],
    ['Total Responses', totalResponses],
    ['Response Rate', `${responseRate}%`],
  ]
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows)
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary')

  // ── Sheet 2: Player Performance ──
  const actHeaders = activities.map(
    a => `${a.sequence_number}. ${a.title} (${ACTIVITY_LABELS[a.activity_type] ?? a.activity_type})`,
  )
  const perfHeader = ['Student', 'Total Score', 'Responses', 'Session (min)', ...actHeaders]
  // Track correctness per player×activity for cell styling
  const perfCorrectness: (boolean | null)[][] = []
  const perfRows = players.map(p => {
    const analytics = playerAnalytics[p.id]
    const totalScore = analytics?.totalScore ??
      Object.values(activityResponses)
        .flatMap(rs => rs.filter(r => r.player_id === p.id))
        .reduce((sum, r) => sum + Number(r.score ?? 0), 0)
    const responseCount = Object.values(activityResponses).filter(rs =>
      rs.some(r => r.player_id === p.id),
    ).length
    const rawSession = analytics?.sessionDuration ?? p.session_duration ?? 0
    const sessionMin = rawSession > 0 ? rawSession : '-'

    const rowCorrectness: (boolean | null)[] = []
    const actCells = activities.map(act => {
      const resp = (activityResponses[act.id] ?? []).find(r => r.player_id === p.id)
      if (!resp) {
        rowCorrectness.push(null)
        return 'No response'
      }
      rowCorrectness.push(isResponseCorrect(act, resp))
      return formatCellValue(act, resp)
    })
    perfCorrectness.push(rowCorrectness)

    return [p.display_name, totalScore, responseCount, sessionMin, ...actCells]
  })
  const perfSheet = XLSX.utils.aoa_to_sheet([perfHeader, ...perfRows])
  styleHeaderRow(perfSheet)
  // Color activity answer cells green/red
  const actStartCol = 4 // columns: Student, Score, Responses, Session, then activities
  perfCorrectness.forEach((row, rowIdx) => {
    row.forEach((correct, colIdx) => {
      styleCorrectnessCell(perfSheet, rowIdx + 1, actStartCol + colIdx, correct)
    })
  })
  XLSX.utils.book_append_sheet(wb, perfSheet, 'Player Performance')

  // ── Sheet 3: Activity Details ──
  const detailHeader = ['Student', 'Activity', 'Type', 'Response', 'Correct?', 'Score', 'Time']
  const detailRows: any[][] = []
  const detailCorrectness: (boolean | null)[] = []
  for (const act of activities) {
    const responses = activityResponses[act.id] ?? []
    for (const p of players) {
      const resp = responses.find(r => r.player_id === p.id)
      detailCorrectness.push(resp ? isResponseCorrect(act, resp) : null)
      detailRows.push([
        p.display_name,
        `${act.sequence_number}. ${act.title}`,
        ACTIVITY_LABELS[act.activity_type] ?? act.activity_type,
        resp ? formatCellValue(act, resp) : 'No response',
        resp ? formatCorrectness(act, resp) : '-',
        resp?.score ?? '-',
        resp ? formatTimeTaken(act, resp) : '-',
      ])
    }
  }
  const detailSheet = XLSX.utils.aoa_to_sheet([detailHeader, ...detailRows])
  styleHeaderRow(detailSheet)
  // Color Response (col 3) and Correct? (col 4) cells
  detailCorrectness.forEach((correct, rowIdx) => {
    styleCorrectnessCell(detailSheet, rowIdx + 1, 3, correct) // Response column
    styleCorrectnessCell(detailSheet, rowIdx + 1, 4, correct) // Correct? column
  })
  XLSX.utils.book_append_sheet(wb, detailSheet, 'Activity Details')

  // ── Download ──
  const filename = `${sanitizeFilename(lesson.name)}_report.xlsx`
  XLSX.writeFile(wb, filename)
}

// ─── PDF Export ──────────────────────────────────────────────────────────────

export async function generatePdfReport(data: ReportData, theme?: PdfTheme): Promise<void> {
  const { lesson, activities, activityResponses, players, playerAnalytics } = data
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const palette = getPdfPalette(theme)
  const tableTheme = getAutoTableThemeStyles(palette)

  // ── Title page ──
  applyPdfPageStyle(doc, palette)
  doc.setFontSize(28)
  doc.setTextColor(...palette.titleColor)
  doc.text('Lesson Report', pageWidth / 2, 50, { align: 'center' })

  doc.setFontSize(18)
  doc.setTextColor(...palette.bodyColor)
  doc.text(lesson.name, pageWidth / 2, 70, { align: 'center' })

  doc.setFontSize(12)
  doc.setTextColor(...palette.subtitleColor)
  doc.text(`Join Code: ${lesson.run_code}`, pageWidth / 2, 85, { align: 'center' })
  doc.text(`Status: ${lesson.status}`, pageWidth / 2, 93, { align: 'center' })
  doc.text(`Date: ${formatDate(lesson.started_at ?? lesson.created_at)}`, pageWidth / 2, 101, { align: 'center' })
  doc.text(`Students: ${players.length}  |  Activities: ${activities.length}`, pageWidth / 2, 109, { align: 'center' })
  doc.text(`Duration: ${formatDurationMinutes(lesson)}`, pageWidth / 2, 117, { align: 'center' })
  doc.setTextColor(...palette.bodyColor)

  // ── Summary statistics ──
  doc.addPage()
  addSectionTitle(doc, 'Summary Statistics', palette)

  const totalResponses = Object.values(activityResponses).flat().length
  const possibleResponses = activities.length * players.length
  const responseRate = possibleResponses > 0 ? Math.round((totalResponses / possibleResponses) * 100) : 0
  const scores = players.map(p => {
    const a = playerAnalytics[p.id]
    return a?.totalScore ?? Object.values(activityResponses)
      .flatMap(rs => rs.filter(r => r.player_id === p.id))
      .reduce((sum, r) => sum + Number(r.score ?? 0), 0)
  })
  const avgScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0
  const topScore = scores.length > 0 ? Math.max(...scores) : 0

  autoTable(doc, {
    startY: 28,
    head: [['Metric', 'Value']],
    body: [
      ['Total Students', String(players.length)],
      ['Total Activities', String(activities.length)],
      ['Total Responses', String(totalResponses)],
      ['Response Rate', `${responseRate}%`],
      ['Average Score', String(avgScore)],
      ['Top Score', String(topScore)],
      ['Duration', formatDurationMinutes(lesson)],
    ],
    theme: 'grid',
    ...tableTheme,
    styles: { ...tableTheme.styles, fontSize: 10 },
  })

  // ── Per-activity pages ──
  for (const act of activities) {
    doc.addPage()
    applyPdfPageStyle(doc, palette)

    // Build context lines for this activity
    const data = act.activity_data ?? {}
    const contextLines: { text: string; size: number; color: typeof palette.bodyColor }[] = []

    // Description or instruction
    const contextText = act.description || act.instruction || data.question || data.prompt || ''
    if (contextText) {
      contextLines.push({ text: String(contextText), size: 10, color: palette.bodyColor })
    }

    // MCQ / Cyber Hygiene / Coding Quiz / Packet Analysis: show options and correct answer
    if (['mcq', 'cyber_hygiene_quiz', 'coding_quiz', 'packet_analysis'].includes(act.activity_type) && data.options?.length) {
      const correctIdx = data.correct_answer != null ? Number(data.correct_answer) : -1
      for (let idx = 0; idx < data.options.length; idx++) {
        const prefix = idx === correctIdx
          ? `[correct] ${String.fromCharCode(65 + idx)})`
          : `${String.fromCharCode(65 + idx)})`
        contextLines.push({
          text: `${prefix} ${data.options[idx]}`,
          size: 9,
          color: palette.subtitleColor,
        })
      }
    }

    // Phishing: show correct answer
    if (act.activity_type === 'phishing_quiz' && data.correct_answer != null) {
      contextLines.push({ text: `Correct answer: ${data.correct_answer}`, size: 9, color: palette.subtitleColor })
    }

    // Coding: show expected output
    if (act.activity_type === 'coding' && data.correct_answer) {
      contextLines.push({ text: `Expected: ${String(data.correct_answer).slice(0, 100)}`, size: 9, color: palette.subtitleColor })
    }

    // Draw title
    doc.setFontSize(16)
    doc.setTextColor(...palette.titleColor)
    doc.text(`${act.sequence_number}. ${act.title} (${ACTIVITY_LABELS[act.activity_type] ?? act.activity_type})`, 14, 20)
    doc.setTextColor(...palette.bodyColor)

    // Draw context
    let contextY = 28
    for (const line of contextLines) {
      doc.setFontSize(line.size)
      doc.setTextColor(...line.color)
      const wrapped = doc.splitTextToSize(line.text, pageWidth - 28)
      doc.text(wrapped, 16, contextY)
      contextY += wrapped.length * (line.size === 10 ? 4.5 : 4) + 2
    }
    if (contextLines.length > 0) contextY += 2

    const responses = activityResponses[act.id] ?? []
    const rows = players.map(p => {
      const resp = responses.find(r => r.player_id === p.id)
      return [
        p.display_name,
        resp ? formatCellValue(act, resp) : 'No response',
        resp ? formatCorrectness(act, resp) : '-',
        resp?.score != null ? String(resp.score) : '-',
        resp ? formatTimeTaken(act, resp) : '-',
      ]
    })

    let isFirstPage = true
    autoTable(doc, {
      startY: contextY,
      head: [['Student', 'Response', 'Correct?', 'Score', 'Time']],
      body: rows,
      theme: 'striped',
      ...tableTheme,
      willDrawPage: (hookData: any) => {
        if (isFirstPage) {
          // Skip — background and context already drawn on this page
          isFirstPage = false
          return
        }
        // Overflow pages: paint background
        const d = hookData.doc
        const w = d.internal.pageSize.getWidth()
        const h = d.internal.pageSize.getHeight()
        d.setFillColor(...palette.pageBg)
        d.rect(0, 0, w, h, 'F')
      },
      styles: { ...tableTheme.styles, fontSize: 9, cellWidth: 'wrap' },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 30 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
      },
    })
  }

  // ── Download ──
  const filename = `${sanitizeFilename(lesson.name)}_report.pdf`
  doc.save(filename)
}

// ─── Course-Level Helpers ───────────────────────────────────────────────────

interface AggregatedStudent {
  displayName: string
  lessonsAttended: number
  perLessonScores: Map<string, number>  // lessonId → score
  totalScore: number
}

function aggregateStudents(lessons: LessonReportData[]): Map<string, AggregatedStudent> {
  const map = new Map<string, AggregatedStudent>()

  for (const lessonData of lessons) {
    const { lesson: lessonObj, players, activityResponses, playerAnalytics } = lessonData
    for (const p of players) {
      const name = p.display_name
      if (!map.has(name)) {
        map.set(name, { displayName: name, lessonsAttended: 0, perLessonScores: new Map(), totalScore: 0 })
      }
      const entry = map.get(name)!
      entry.lessonsAttended++
      const score = playerAnalytics[p.id]?.totalScore ??
        Object.values(activityResponses)
          .flatMap(rs => rs.filter(r => r.player_id === p.id))
          .reduce((sum, r) => sum + Number(r.score ?? 0), 0)
      entry.perLessonScores.set(lessonObj.id, score)
      entry.totalScore += score
    }
  }
  return map
}

function truncateSheetName(name: string, maxLen = 31): string {
  if (name.length <= maxLen) return name
  return name.slice(0, maxLen - 1) + '…'
}

// ─── Course Excel Export ────────────────────────────────────────────────────

export async function generateCourseExcelReport(data: CourseReportData): Promise<void> {
  const { course, lessons } = data
  const wb = XLSX.utils.book_new()
  const students = aggregateStudents(lessons)

  const allPlayers = lessons.flatMap(l => l.players)
  const uniqueStudentCount = students.size
  const totalResponses = lessons.reduce(
    (sum, l) => sum + Object.values(l.activityResponses).flat().length, 0,
  )
  const totalActivities = lessons.reduce((sum, l) => sum + l.activities.length, 0)
  const possibleResponses = lessons.reduce(
    (sum, l) => sum + l.activities.length * l.players.length, 0,
  )
  const responseRate = possibleResponses > 0 ? Math.round((totalResponses / possibleResponses) * 100) : 0

  // ── Sheet 1: Course Summary ──
  const summaryRows = [
    ['Course Report'],
    [],
    ['Course Name', course.name],
    ['Course Code', course.course_code],
    ['Status', course.status],
    [],
    ['Total Lessons', lessons.length],
    ['Unique Students', uniqueStudentCount],
    ['Total Activities', totalActivities],
    ['Total Responses', totalResponses],
    ['Overall Response Rate', `${responseRate}%`],
  ]
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Course Summary')

  // ── Sheet 2: Student Overview ──
  const lessonHeaders = lessons.map(l => l.lesson.name)
  const overviewHeader = ['Student', 'Lessons Attended', 'Total Score', 'Avg Score/Lesson', ...lessonHeaders]
  const overviewRows = Array.from(students.values())
    .sort((a, b) => b.totalScore - a.totalScore)
    .map(s => {
      const avg = s.lessonsAttended > 0 ? Math.round(s.totalScore / s.lessonsAttended) : 0
      const perLesson = lessons.map(l => {
        const score = s.perLessonScores.get(l.lesson.id)
        return score !== undefined ? score : '-'
      })
      return [s.displayName, s.lessonsAttended, s.totalScore, avg, ...perLesson]
    })
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([overviewHeader, ...overviewRows]), 'Student Overview')

  // ── One sheet per lesson ──
  for (const lessonData of lessons) {
    const { lesson: lessonObj, activities, activityResponses, players, playerAnalytics } = lessonData
    const sheetName = truncateSheetName(`Lesson - ${lessonObj.name}`)

    const actHeaders = activities.map(
      a => `${a.sequence_number}. ${a.title} (${ACTIVITY_LABELS[a.activity_type] ?? a.activity_type})`,
    )
    const perfHeader = ['Student', 'Score', 'Responses', 'Session (min)', ...actHeaders]
    const courseCorrectness: (boolean | null)[][] = []
    const perfRows = players.map(p => {
      const analytics = playerAnalytics[p.id]
      const totalScore = analytics?.totalScore ??
        Object.values(activityResponses)
          .flatMap(rs => rs.filter(r => r.player_id === p.id))
          .reduce((sum, r) => sum + Number(r.score ?? 0), 0)
      const responseCount = Object.values(activityResponses).filter(rs =>
        rs.some(r => r.player_id === p.id),
      ).length
      const rawSession = analytics?.sessionDuration ?? p.session_duration ?? 0
      const sessionMin = rawSession > 0 ? rawSession : '-'

      const rowCorrectness: (boolean | null)[] = []
      const actCells = activities.map(act => {
        const resp = (activityResponses[act.id] ?? []).find(r => r.player_id === p.id)
        if (!resp) {
          rowCorrectness.push(null)
          return 'No response'
        }
        rowCorrectness.push(isResponseCorrect(act, resp))
        return formatCellValue(act, resp)
      })
      courseCorrectness.push(rowCorrectness)
      return [p.display_name, totalScore, responseCount, sessionMin, ...actCells]
    })
    const lessonPerfSheet = XLSX.utils.aoa_to_sheet([perfHeader, ...perfRows])
    styleHeaderRow(lessonPerfSheet)
    const lessonActStartCol = 4
    courseCorrectness.forEach((row, rowIdx) => {
      row.forEach((correct, colIdx) => {
        styleCorrectnessCell(lessonPerfSheet, rowIdx + 1, lessonActStartCol + colIdx, correct)
      })
    })
    XLSX.utils.book_append_sheet(wb, lessonPerfSheet, sheetName)
  }

  // ── All Activity Details ──
  const allDetailHeader = ['Student', 'Lesson', 'Activity', 'Type', 'Response', 'Correct?', 'Score', 'Time']
  const allDetailRows: any[][] = []
  const allDetailCorrectness: (boolean | null)[] = []
  for (const lessonData of lessons) {
    for (const act of lessonData.activities) {
      const responses = lessonData.activityResponses[act.id] ?? []
      for (const p of lessonData.players) {
        const resp = responses.find(r => r.player_id === p.id)
        allDetailCorrectness.push(resp ? isResponseCorrect(act, resp) : null)
        allDetailRows.push([
          p.display_name,
          lessonData.lesson.name,
          `${act.sequence_number}. ${act.title}`,
          ACTIVITY_LABELS[act.activity_type] ?? act.activity_type,
          resp ? formatCellValue(act, resp) : 'No response',
          resp ? formatCorrectness(act, resp) : '-',
          resp?.score ?? '-',
          resp ? formatTimeTaken(act, resp) : '-',
        ])
      }
    }
  }
  const allDetailSheet = XLSX.utils.aoa_to_sheet([allDetailHeader, ...allDetailRows])
  styleHeaderRow(allDetailSheet)
  allDetailCorrectness.forEach((correct, rowIdx) => {
    styleCorrectnessCell(allDetailSheet, rowIdx + 1, 4, correct) // Response column
    styleCorrectnessCell(allDetailSheet, rowIdx + 1, 5, correct) // Correct? column
  })
  XLSX.utils.book_append_sheet(wb, allDetailSheet, 'All Activity Details')

  const filename = `${sanitizeFilename(course.name)}_course_report.xlsx`
  XLSX.writeFile(wb, filename)
}

// ─── Course PDF Export ──────────────────────────────────────────────────────

export interface RunReportData {
  runTitle: string
  lessons: LessonReportData[]
}

export async function generateRunExcelReport(data: RunReportData): Promise<void> {
  const { runTitle, lessons } = data
  const wb = XLSX.utils.book_new()
  const students = aggregateStudents(lessons)

  const totalResponses = lessons.reduce(
    (sum, l) => sum + Object.values(l.activityResponses).flat().length, 0,
  )
  const totalActivities = lessons.reduce((sum, l) => sum + l.activities.length, 0)
  const possibleResponses = lessons.reduce(
    (sum, l) => sum + l.activities.length * l.players.length, 0,
  )
  const responseRate = possibleResponses > 0 ? Math.round((totalResponses / possibleResponses) * 100) : 0

  // Sheet 1: Run Summary
  const summaryRows = [
    ['Run Report'],
    [],
    ['Run Title', runTitle],
    ['Total Lessons', lessons.length],
    ['Unique Students', students.size],
    ['Total Activities', totalActivities],
    ['Total Responses', totalResponses],
    ['Overall Response Rate', `${responseRate}%`],
  ]
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Run Summary')

  // Sheet 2: Student Overview
  const lessonHeaders = lessons.map(l => l.lesson.name)
  const overviewHeader = ['Student', 'Lessons Attended', 'Total Score', 'Avg Score/Lesson', ...lessonHeaders]
  const overviewRows = Array.from(students.values())
    .sort((a, b) => b.totalScore - a.totalScore)
    .map(s => {
      const avg = s.lessonsAttended > 0 ? Math.round(s.totalScore / s.lessonsAttended) : 0
      const perLesson = lessons.map(l => {
        const score = s.perLessonScores.get(l.lesson.id)
        return score !== undefined ? score : '-'
      })
      return [s.displayName, s.lessonsAttended, s.totalScore, avg, ...perLesson]
    })
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([overviewHeader, ...overviewRows]), 'Student Overview')

  // One sheet per lesson
  for (const lessonData of lessons) {
    const { lesson: lessonObj, activities, activityResponses, players, playerAnalytics } = lessonData
    const sheetName = truncateSheetName(`Lesson - ${lessonObj.name}`)

    const actHeaders = activities.map(
      a => `${a.sequence_number}. ${a.title} (${ACTIVITY_LABELS[a.activity_type] ?? a.activity_type})`,
    )
    const perfHeader = ['Student', 'Score', 'Responses', 'Session (min)', ...actHeaders]
    const runCorrectness: (boolean | null)[][] = []
    const perfRows = players.map(p => {
      const analytics = playerAnalytics[p.id]
      const totalScore = analytics?.totalScore ??
        Object.values(activityResponses)
          .flatMap(rs => rs.filter(r => r.player_id === p.id))
          .reduce((sum, r) => sum + Number(r.score ?? 0), 0)
      const responseCount = Object.values(activityResponses).filter(rs =>
        rs.some(r => r.player_id === p.id),
      ).length
      const rawSession = analytics?.sessionDuration ?? p.session_duration ?? 0
      const sessionMin = rawSession > 0 ? rawSession : '-'

      const rowCorrectness: (boolean | null)[] = []
      const actCells = activities.map(act => {
        const resp = (activityResponses[act.id] ?? []).find(r => r.player_id === p.id)
        if (!resp) {
          rowCorrectness.push(null)
          return 'No response'
        }
        rowCorrectness.push(isResponseCorrect(act, resp))
        return formatCellValue(act, resp)
      })
      runCorrectness.push(rowCorrectness)
      return [p.display_name, totalScore, responseCount, sessionMin, ...actCells]
    })
    const lessonPerfSheet = XLSX.utils.aoa_to_sheet([perfHeader, ...perfRows])
    styleHeaderRow(lessonPerfSheet)
    const lessonActStartCol = 4
    runCorrectness.forEach((row, rowIdx) => {
      row.forEach((correct, colIdx) => {
        styleCorrectnessCell(lessonPerfSheet, rowIdx + 1, lessonActStartCol + colIdx, correct)
      })
    })
    XLSX.utils.book_append_sheet(wb, lessonPerfSheet, sheetName)
  }

  const filename = `${sanitizeFilename(runTitle)}_run_report.xlsx`
  XLSX.writeFile(wb, filename)
}

export async function generateRunPdfReport(data: RunReportData, theme?: PdfTheme): Promise<void> {
  const { runTitle, lessons } = data
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const palette = getPdfPalette(theme)
  const tableTheme = getAutoTableThemeStyles(palette)
  const students = aggregateStudents(lessons)

  const uniqueStudentCount = students.size
  const totalResponses = lessons.reduce(
    (sum, l) => sum + Object.values(l.activityResponses).flat().length, 0,
  )
  const totalActivities = lessons.reduce((sum, l) => sum + l.activities.length, 0)
  const possibleResponses = lessons.reduce(
    (sum, l) => sum + l.activities.length * l.players.length, 0,
  )
  const responseRate = possibleResponses > 0 ? Math.round((totalResponses / possibleResponses) * 100) : 0

  // Title page
  applyPdfPageStyle(doc, palette)
  doc.setFontSize(28)
  doc.setTextColor(...palette.titleColor)
  doc.text('Run Report', pageWidth / 2, 50, { align: 'center' })
  doc.setFontSize(18)
  doc.setTextColor(...palette.bodyColor)
  doc.text(runTitle, pageWidth / 2, 70, { align: 'center' })
  doc.setFontSize(12)
  doc.setTextColor(...palette.subtitleColor)
  doc.text(`Lessons: ${lessons.length}  |  Students: ${uniqueStudentCount}`, pageWidth / 2, 90, { align: 'center' })
  doc.setTextColor(...palette.bodyColor)

  // Summary
  doc.addPage()
  addSectionTitle(doc, 'Run Summary', palette)

  const allScores = Array.from(students.values()).map(s => s.totalScore)
  const avgScore = allScores.length > 0 ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : 0

  autoTable(doc, {
    startY: 28,
    head: [['Metric', 'Value']],
    body: [
      ['Total Lessons', String(lessons.length)],
      ['Unique Students', String(uniqueStudentCount)],
      ['Total Activities', String(totalActivities)],
      ['Total Responses', String(totalResponses)],
      ['Overall Response Rate', `${responseRate}%`],
      ['Average Total Score', String(avgScore)],
    ],
    theme: 'grid',
    ...tableTheme,
    styles: { ...tableTheme.styles, fontSize: 10 },
  })

  // Student overview
  doc.addPage()
  addSectionTitle(doc, 'Student Overview', palette)

  const studentRows = Array.from(students.values())
    .sort((a, b) => b.totalScore - a.totalScore)
    .map(s => {
      const avg = s.lessonsAttended > 0 ? Math.round(s.totalScore / s.lessonsAttended) : 0
      return [s.displayName, String(s.lessonsAttended), String(s.totalScore), String(avg)]
    })

  autoTable(doc, {
    startY: 28,
    head: [['Student', 'Lessons Attended', 'Total Score', 'Avg Score/Lesson']],
    body: studentRows.length > 0 ? studentRows : [['No students', '-', '-', '-']],
    theme: 'striped',
    ...tableTheme,
    styles: { ...tableTheme.styles, fontSize: 9 },
  })

  // Per-lesson pages with activity detail
  for (const lessonData of lessons) {
    const { lesson: lessonObj, activities, activityResponses, players, playerAnalytics } = lessonData

    // Lesson summary page
    doc.addPage()
    addSectionTitle(doc, `Lesson: ${lessonObj.name}`, palette)
    doc.setFontSize(12)
    doc.setTextColor(...palette.subtitleColor)
    doc.text('Student Summary', 14, 24)

    const lessonRows = players.map(p => {
      const analytics = playerAnalytics[p.id]
      const score = analytics?.totalScore ??
        Object.values(activityResponses)
          .flatMap(rs => rs.filter(r => r.player_id === p.id))
          .reduce((sum, r) => sum + Number(r.score ?? 0), 0)
      const responseCount = Object.values(activityResponses).filter(rs =>
        rs.some(r => r.player_id === p.id),
      ).length
      return [p.display_name, String(score), String(responseCount)]
    })

    autoTable(doc, {
      startY: 32,
      head: [['Student', 'Score', 'Responses']],
      body: lessonRows.length > 0 ? lessonRows : [['No students', '-', '-']],
      theme: 'striped',
      ...tableTheme,
      styles: { ...tableTheme.styles, fontSize: 9 },
    })

    // Per-activity pages for this lesson
    for (const act of activities) {
      doc.addPage()
      applyPdfPageStyle(doc, palette)

      const actData = act.activity_data ?? {}
      const contextLines: { text: string; size: number; color: typeof palette.bodyColor }[] = []

      const contextText = act.description || act.instruction || actData.question || actData.prompt || ''
      if (contextText) {
        contextLines.push({ text: String(contextText), size: 10, color: palette.bodyColor })
      }

      if (['mcq', 'cyber_hygiene_quiz', 'coding_quiz', 'packet_analysis'].includes(act.activity_type) && actData.options?.length) {
        const correctIdx = actData.correct_answer != null ? Number(actData.correct_answer) : -1
        for (let idx = 0; idx < actData.options.length; idx++) {
          const prefix = idx === correctIdx
            ? `[correct] ${String.fromCharCode(65 + idx)})`
            : `${String.fromCharCode(65 + idx)})`
          contextLines.push({
            text: `${prefix} ${actData.options[idx]}`,
            size: 9,
            color: palette.subtitleColor,
          })
        }
      }

      if (act.activity_type === 'phishing_quiz' && actData.correct_answer != null) {
        contextLines.push({ text: `Correct answer: ${actData.correct_answer}`, size: 9, color: palette.subtitleColor })
      }

      if (act.activity_type === 'coding' && actData.correct_answer) {
        contextLines.push({ text: `Expected: ${String(actData.correct_answer).slice(0, 100)}`, size: 9, color: palette.subtitleColor })
      }

      // Draw title
      doc.setFontSize(14)
      doc.setTextColor(...palette.subtitleColor)
      doc.text(lessonObj.name, 14, 14)
      doc.setFontSize(16)
      doc.setTextColor(...palette.titleColor)
      doc.text(`${act.sequence_number}. ${act.title} (${ACTIVITY_LABELS[act.activity_type] ?? act.activity_type})`, 14, 22)
      doc.setTextColor(...palette.bodyColor)

      // Draw context
      let contextY = 30
      for (const line of contextLines) {
        doc.setFontSize(line.size)
        doc.setTextColor(...line.color)
        const wrapped = doc.splitTextToSize(line.text, pageWidth - 28)
        doc.text(wrapped, 16, contextY)
        contextY += wrapped.length * (line.size === 10 ? 4.5 : 4) + 2
      }
      if (contextLines.length > 0) contextY += 2

      const responses = activityResponses[act.id] ?? []
      const actRows = players.map(p => {
        const resp = responses.find(r => r.player_id === p.id)
        return [
          p.display_name,
          resp ? formatCellValue(act, resp) : 'No response',
          resp ? formatCorrectness(act, resp) : '-',
          resp?.score != null ? String(resp.score) : '-',
          resp ? formatTimeTaken(act, resp) : '-',
        ]
      })

      let isFirstPage = true
      autoTable(doc, {
        startY: contextY,
        head: [['Student', 'Response', 'Correct?', 'Score', 'Time']],
        body: actRows.length > 0 ? actRows : [['No responses', '-', '-', '-', '-']],
        theme: 'striped',
        ...tableTheme,
        willDrawPage: (hookData: any) => {
          if (isFirstPage) {
            isFirstPage = false
            return
          }
          const d = hookData.doc
          const w = d.internal.pageSize.getWidth()
          const h = d.internal.pageSize.getHeight()
          d.setFillColor(...palette.pageBg)
          d.rect(0, 0, w, h, 'F')
        },
        styles: { ...tableTheme.styles, fontSize: 9, cellWidth: 'wrap' },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 30 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
        },
      })
    }
  }

  const filename = `${sanitizeFilename(runTitle)}_run_report.pdf`
  doc.save(filename)
}

export async function generateCoursePdfReport(data: CourseReportData, theme?: PdfTheme): Promise<void> {
  const { course, lessons } = data
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const palette = getPdfPalette(theme)
  const tableTheme = getAutoTableThemeStyles(palette)
  const students = aggregateStudents(lessons)

  const uniqueStudentCount = students.size
  const totalResponses = lessons.reduce(
    (sum, l) => sum + Object.values(l.activityResponses).flat().length, 0,
  )
  const totalActivities = lessons.reduce((sum, l) => sum + l.activities.length, 0)
  const possibleResponses = lessons.reduce(
    (sum, l) => sum + l.activities.length * l.players.length, 0,
  )
  const responseRate = possibleResponses > 0 ? Math.round((totalResponses / possibleResponses) * 100) : 0

  // ── Title page ──
  applyPdfPageStyle(doc, palette)
  doc.setFontSize(28)
  doc.setTextColor(...palette.titleColor)
  doc.text('Course Report', pageWidth / 2, 50, { align: 'center' })

  doc.setFontSize(18)
  doc.setTextColor(...palette.bodyColor)
  doc.text(course.name, pageWidth / 2, 70, { align: 'center' })

  doc.setFontSize(12)
  doc.setTextColor(...palette.subtitleColor)
  doc.text(`Course Code: ${course.course_code}`, pageWidth / 2, 85, { align: 'center' })
  doc.text(`Status: ${course.status}`, pageWidth / 2, 93, { align: 'center' })
  doc.text(`Lessons: ${lessons.length}  |  Students: ${uniqueStudentCount}`, pageWidth / 2, 101, { align: 'center' })
  doc.setTextColor(...palette.bodyColor)

  // ── Course Summary Statistics ──
  doc.addPage()
  addSectionTitle(doc, 'Course Summary', palette)

  const allScores = Array.from(students.values()).map(s => s.totalScore)
  const avgScore = allScores.length > 0 ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : 0
  const topScore = allScores.length > 0 ? Math.max(...allScores) : 0

  autoTable(doc, {
    startY: 28,
    head: [['Metric', 'Value']],
    body: [
      ['Total Lessons', String(lessons.length)],
      ['Unique Students', String(uniqueStudentCount)],
      ['Total Activities', String(totalActivities)],
      ['Total Responses', String(totalResponses)],
      ['Overall Response Rate', `${responseRate}%`],
      ['Average Total Score', String(avgScore)],
      ['Top Total Score', String(topScore)],
    ],
    theme: 'grid',
    ...tableTheme,
    styles: { ...tableTheme.styles, fontSize: 10 },
  })

  // ── Student Overview ──
  doc.addPage()
  addSectionTitle(doc, 'Student Overview (across all lessons)', palette)

  const studentRows = Array.from(students.values())
    .sort((a, b) => b.totalScore - a.totalScore)
    .map(s => {
      const avg = s.lessonsAttended > 0 ? Math.round(s.totalScore / s.lessonsAttended) : 0
      return [s.displayName, String(s.lessonsAttended), String(s.totalScore), String(avg)]
    })

  autoTable(doc, {
    startY: 28,
    head: [['Student', 'Lessons Attended', 'Total Score', 'Avg Score/Lesson']],
    body: studentRows,
    theme: 'striped',
    ...tableTheme,
    styles: { ...tableTheme.styles, fontSize: 9 },
  })

  // ── Per-lesson pages ──
  for (const lessonData of lessons) {
    const { lesson: lessonObj, activities, activityResponses, players, playerAnalytics } = lessonData
    doc.addPage()
    addSectionTitle(doc, `Lesson: ${lessonObj.name}`, palette)

    const rows = players.map(p => {
      const analytics = playerAnalytics[p.id]
      const score = analytics?.totalScore ??
        Object.values(activityResponses)
          .flatMap(rs => rs.filter(r => r.player_id === p.id))
          .reduce((sum, r) => sum + Number(r.score ?? 0), 0)
      const responseCount = Object.values(activityResponses).filter(rs =>
        rs.some(r => r.player_id === p.id),
      ).length
      const rawSession = analytics?.sessionDuration ?? p.session_duration ?? 0
      const sessionMin = rawSession > 0 ? rawSession : '-'
      return [p.display_name, String(score), String(responseCount), String(sessionMin)]
    })

    autoTable(doc, {
      startY: 28,
      head: [['Student', 'Score', 'Responses', 'Session (min)']],
      body: rows.length > 0 ? rows : [['No students', '-', '-', '-']],
      theme: 'striped',
      ...tableTheme,
      styles: { ...tableTheme.styles, fontSize: 9 },
    })
  }

  const filename = `${sanitizeFilename(course.name)}_course_report.pdf`
  doc.save(filename)
}
